import { useCallback, useMemo } from 'react';
import { Cloudinary } from '@cloudinary/url-gen';
import { fill, limitFill, pad } from '@cloudinary/url-gen/actions/resize';
import { focusOn } from '@cloudinary/url-gen/qualifiers/gravity';
import { dpr } from '@cloudinary/url-gen/actions/delivery';
import { face, faces } from '@cloudinary/url-gen/qualifiers/focusOn';
import useSelector from '../../redux/typedHooks';
import { FocusMethod, FormatType, ResizeMethod } from '../../../../types/CloudinaryImageType';

interface CldProps {
  width?: number;
  height?: number;
  format?: FormatType;
  quality?: string;
  resizeMethod?: ResizeMethod;
  focusMethod?: FocusMethod;
  aspectRatio?: string;
  devicePR?: number;
}

const cloudinaryDefaultImageHeight = 432;
const cloudinaryDefaultImageWidth = 768;

export const useCloudinary = () => {
  const imageCloudName = useSelector((state) => state.application?.imageCloudName);

  let devicePixelRatio = 1;
  if (typeof window !== 'undefined' && window.devicePixelRatio) {
    devicePixelRatio = window.devicePixelRatio;
  }

  const cld = useMemo(() => {
    if (imageCloudName) {
      return new Cloudinary({
        cloud: {
          cloudName: imageCloudName,
        },
        url: {
          secure: true,
          analytics: false,
        },
      });
    }
    return null;
  }, [imageCloudName]);

  const getImage = useCallback(
    (publicId: string, format?: string) => {
      if (!cld) return null;
      const img = cld.image(publicId);
      img.format(format || 'auto');
      return img;
    },
    [cld],
  );

  const getResizeMethod = (resizeMethod: ResizeMethod) => {
    switch (resizeMethod) {
      case 'limitFill':
        return limitFill();
      case 'pad':
        return pad();
      default:
        return fill();
    }
  };

  const getGravityMethod = (focusMethod: FocusMethod) => {
    switch (focusMethod) {
      case 'faces':
        return focusOn(faces());
      case 'face':
        return focusOn(face());

      default:
        return focusOn(faces());
    }
  };

  const getProcessedImage = useCallback(
    (
      publicId: string,
      { height, width, format, focusMethod, quality, resizeMethod = 'fill', aspectRatio }: CldProps = {},
    ) => {
      if (!cld || !publicId) return null;
      const resize = getResizeMethod(resizeMethod);

      if (width) resize.width(width);
      if (height) resize.height(height);
      if (!height && !width) resize.height(cloudinaryDefaultImageHeight).width(cloudinaryDefaultImageWidth);
      if (focusMethod) resize.gravity(getGravityMethod(focusMethod));
      if (aspectRatio) resize.aspectRatio(aspectRatio);

      const img = cld.image(publicId).resize(resize);
      return img.quality(quality || 'auto:best').format(format || 'auto');
    },
    [cld],
  );

  const getResponsiveImage = useCallback(
    (
      publicId: string,
      {
        width,
        height,
        format,
        quality,
        focusMethod,
        resizeMethod,
        devicePR = devicePixelRatio,
        aspectRatio,
      }: CldProps = {},
    ) => {
      if (!cld || !publicId) return null;

      const img = cld.image(publicId);
      if (height || width) {
        const resize = getResizeMethod(aspectRatio ? 'fill' : resizeMethod);
        if (width) resize.width(width);
        if (height) resize.height(height);
        if (focusMethod) resize.gravity(getGravityMethod(focusMethod));
        if (aspectRatio) resize.aspectRatio(aspectRatio);
        img.resize(resize);
      }

      return img
        .delivery(dpr(devicePR))
        .quality(quality || 'auto:best')
        .format(format || 'auto');
    },
    [cld, devicePixelRatio],
  );

  const getLogo = useCallback(
    (publicId: string, { width, height, quality, format }: CldProps = {}) => {
      if (!cld || !publicId) return null;

      const image = cld.image(publicId);

      const resize = limitFill();
      if (width || height) {
        if (width) resize.width(width);
        if (height) resize.height(height);
      } else {
        resize.width(56).height(56);
      }
      image.resize(resize);

      return image.quality(quality || 'auto:best').format(format || 'svg');
    },
    [cld],
  );

  const getVideo = useCallback(
    (publicId: string, { width, resizeMethod }: CldProps = {}) => {
      if (!cld || !publicId) return null;
      const video = cld.video(publicId);

      if (width || resizeMethod) {
        const resize = resizeMethod === 'limitFill' ? limitFill() : fill();
        if (width) resize.width(width);
        video.resize(resize);
      }

      return video;
    },
    [cld],
  );

  const getResponsiveVideo = useCallback(
    (publicId: string, { width, height, format, quality }: CldProps = {}) => {
      if (!cld || !publicId) return null;

      const video = cld.video(publicId);
      if (width || height) {
        const resize = fill();
        if (width) resize.width(Math.round(width / devicePixelRatio));
        if (height) resize.height(Math.round(height / devicePixelRatio));
        video.resize(resize);
      }

      return video
        .delivery(dpr(devicePixelRatio))
        .quality(quality || 'auto:best')
        .format(format || 'auto');
    },
    [cld, devicePixelRatio],
  );

  return {
    cld,
    getImage,
    getProcessedImage,
    getResponsiveImage,
    getLogo,
    getVideo,
    getResponsiveVideo,
  };
};
